import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import ProtectedRoute from './protected-route';
import OrgRedirect from './org-redirect';
import { Layout } from '@/layout';
import { AdminPage } from '@/pages/admin/settings/admin-settings-page';
import { ComposerDashboardPage } from '@/pages/composer-dashboard/composer-dashboard-page';
import { CapitolKeysPage } from '@/pages/api-keys/capitol-keys-page';
import { ApiTokensPage } from '@/pages/api-tokens/api-tokens-page';
import { ConnectorsPage } from '@/pages/connectors/connectors-page';

// swagger-ui-react adds ~1MB to the JS bundle; lazy-load so only users
// visiting /docs/workflow-api pay that cost.
const WorkflowApiPage = lazy(() =>
  import('@/pages/docs/workflow-api-page').then((m) => ({
    default: m.WorkflowApiPage,
  })),
);
import { GuardrailsPage } from '@/pages/guardrails/guardrails-page';
import { Login } from '@/pages/login/login';
import { MembersByOrgPage } from '@/pages/admin/settings/members-admin-view';
import { NotFoundPage } from '@/pages/not-found';
import { GeneralSettingsPage } from '@/pages/org-settings/general/general-settings-page';
import { OrgNodesSettingsPage } from '@/pages/org-settings/nodes/org-nodes-settings-page';
import { OrgDetailsPage } from '@/pages/admin/org-details/org-details-page';
import { AnalyticsConfigPage } from '@/pages/admin/analytics-config/analytics-config-page';
import { CostPage } from '@/pages/admin/cost/cost-page';
import { InviteMemberRoute } from './invite-member-route';
import { LoginRoute } from './login-route';
import { AdminRoute } from './admin-route';
import { OwnerOrAdminRoute } from './owner-or-admin-route';
import { MembersPage } from '@/pages/org-settings/members/members-page';
import { ROUTES } from '@/constants/routes';
import { DataSourcesPage } from '@/pages/data-sources/data-sources-page';
import { SourcesPage } from '@/pages/sources/sources-page';
import EvaluationDashboard from '@/pages/evaluation/evaluations';
import EvaluationConfigPage from '@/pages/evaluation/evaluation-config-page';
import TestsPage from '@/pages/evaluation/tests';
import { RouterWithErrorBoundary } from '@/components/error-boundary';
import { useContext } from 'react';
import { AuthContext } from '@/context/auth-context';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { FeatureGuard } from '@/components/feature-guard';
import { WorkflowDashboardPage } from '@/pages/workflow/workflow-dashboard-page';
import { WorkflowEditor } from '@/pages/workflow/workflow-editor';
import TestsConfigPage from '@/pages/evaluation/tests-config-page';
import { SkillsPage } from '@/pages/skills/skills-page';
import { MyToolsPage } from '@/pages/my-tools/my-tools-page';
import { McpServersPage } from '@/pages/org-settings/mcp-servers/mcp-servers-page';
import { McpServerDetailPage } from '@/pages/org-settings/mcp-servers/mcp-server-detail-page';
import { TriggersPage } from '@/pages/triggers/triggers-page';
import { ArtifactsPage } from '@/pages/artifacts/artifacts-page';
import { ArtifactViewerPage } from '@/pages/artifacts/artifact-viewer-page';
import { FilesPage } from '@/pages/files/files-page';
import { FileBrowserPage } from '@/pages/files/file-browser-page';
import { SchedulesPage } from '@/pages/schedules/schedules-page';
import { CollectionPage } from '@/pages/data-sources/collection-page';
import { ContextGraphsPage } from '@/pages/context-graphs/context-graphs-page';
import { ConditionalLandingRedirect } from '@/components/conditional-landing-redirect';
import { Auth0Callback } from '@/pages/callback/auth0-callback';
import { WorkflowBuilderPage } from '@/pages/workflow-builder/workflow-builder-page';
import { WorkflowAgentConfigPage } from '@/pages/admin/workflow-agent/workflow-agent-config-page';
import { AgentsListPage } from '@/pages/agents/agents-list-page';
import { ChatHomePage } from '@/pages/chat-home/chat-home-page';
import { ChatsPage } from '@/pages/chats/chats-page';
import { AgentConfigPage } from '@/pages/agents/agent-config-page';
import { AgentChatPage } from '@/pages/agents/agent-chat-page';
import { LabelsPage } from '@/pages/org-settings/labels/labels-page';
import { EncryptionSettingsPage } from '@/pages/org-settings/encryption/encryption-settings-page';
import { A2AIpAllowlistPage } from '@/pages/org-settings/a2a-ip-allowlist/a2a-ip-allowlist-page';
import { InfraAdminRoute } from './infra-admin-route';
import { UatBoardPage } from '@/pages/admin/uat/uat-board-page';
import { EmbedChatPage } from '@/pages/embed/embed-chat-page';
import { EmbedSearchPage } from '@/pages/embed/embed-search-page';
import OrgSyncGuard from '@/components/org-sync-guard';
import { ChatAgentsListPage } from '@/pages/chat-agents/chat-agents-list-page';
import { ChatAgentConfigPage } from '@/pages/chat-agents/chat-agent-config-page';
import { ChatAgentChatPage } from '@/pages/chat-agents/chat-agent-chat-page';
import { ORG_PREFIX } from '@/constants/routes';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const MainRoutesContent = () => {
  const { user } = useContext(AuthContext);
  const {
    isEvalsEnabled,
    isWorkflowEnabled,
    isDataSourcesEnabled,
    isContextGraphsEnabled,
    isComposerDashboardEnabled,
    isChatAgentsEnabled,
    isAgentsEnabled,
    isPrimaryChatEnabled,
    isChatsPageEnabled,
    isSourcesEnabled,
    isBuilderAgentOwnerOnly,
    isOrgNodesToolsEnabled,
    isOrgMcpToolsEnabled,
    isTriggersEnabled,
    isLabelsEnabled,
    isDocsWorkflowApiEnabled,
    isApiKeysEnabled,
    isQualificationEnabled,
    isUserApiTokensEnabled,
    isSkillsEnabled,
    isArtifactsEnabled,
    isFilesEnabled,
    isScheduleEnabled,
  } = useLaunchDarkly();

  return (
    <RouterWithErrorBoundary>
      <SentryRoutes>
        {/* Public routes (no org context needed) */}
        <Route path='/callback' element={<Auth0Callback />} />
        <Route path='/org/invite' element={<InviteMemberRoute />} />
        {/*
          /oauth/connected has no route: main.tsx mounts it outside this tree.
          As a route it sat inside LaunchDarklyWrapper's auth-boot spinner, which
          delayed the consent report and lost it if the popup was closed first.
        */}
        <Route path={ROUTES.EMBED.CHAT} element={<EmbedChatPage />} />
        <Route path={ROUTES.EMBED.SEARCH} element={<EmbedSearchPage />} />
        <Route path={ROUTES.LOGIN} element={<LoginRoute />}>
          <Route index element={<Login />} />
        </Route>

        {/* Org-scoped routes: /o/:orgId/... */}
        <Route path='/' element={<ProtectedRoute />}>
          <Route path={ORG_PREFIX} element={<OrgSyncGuard />}>
            <Route element={<Layout />}>
              <Route index element={<ConditionalLandingRedirect />} />
              <Route
                path='chat-home/:conversationId?'
                element={
                  <FeatureGuard isEnabled={isPrimaryChatEnabled}>
                    <ChatHomePage />
                  </FeatureGuard>
                }
              />
              <Route
                path='chats'
                element={
                  <FeatureGuard isEnabled={isChatsPageEnabled}>
                    <ChatsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='workflow-builder'
                element={
                  <FeatureGuard
                    isEnabled={
                      isChatAgentsEnabled &&
                      (!isBuilderAgentOwnerOnly ||
                        !!user?.isOwner ||
                        !!user?.isAdmin)
                    }
                  >
                    <WorkflowBuilderPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='chat-agents'
                element={
                  <FeatureGuard isEnabled={isChatAgentsEnabled}>
                    <ChatAgentsListPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='chat-agents/new'
                element={
                  <FeatureGuard isEnabled={isChatAgentsEnabled}>
                    <ChatAgentConfigPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='chat-agents/:agentId/config'
                element={
                  <FeatureGuard isEnabled={isChatAgentsEnabled}>
                    <ChatAgentConfigPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='chat-agents/:agentId/chat'
                element={
                  <FeatureGuard isEnabled={isChatAgentsEnabled}>
                    <ChatAgentChatPage />
                  </FeatureGuard>
                }
              />
              {/* A2A agent management (list, create, configure) is
                  owner/admin-only and its nav entry already lives in the
                  owner/admin Organization Settings section, so members have no
                  path here. Members mint orchestrator agents from the workflow
                  list instead (the bearer is shown in that modal). Per-agent
                  chat below stays reachable. */}
              <Route element={<OwnerOrAdminRoute />}>
                <Route
                  path='agents'
                  element={
                    <FeatureGuard isEnabled={isAgentsEnabled}>
                      <AgentsListPage />
                    </FeatureGuard>
                  }
                />
                <Route
                  path='agents/new'
                  element={
                    <FeatureGuard isEnabled={isAgentsEnabled}>
                      <AgentConfigPage />
                    </FeatureGuard>
                  }
                />
                <Route
                  path='agents/:agentId/config'
                  element={
                    <FeatureGuard isEnabled={isAgentsEnabled}>
                      <AgentConfigPage />
                    </FeatureGuard>
                  }
                />
              </Route>
              <Route
                path='agents/:agentId/chat'
                element={
                  <FeatureGuard isEnabled={isAgentsEnabled}>
                    <AgentChatPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='workflow'
                element={<FeatureGuard isEnabled={isWorkflowEnabled} />}
              >
                <Route index element={<WorkflowDashboardPage />} />
                <Route path=':workflowId' element={<WorkflowEditor />} />
              </Route>
              <Route
                path='keys/capitol'
                element={<Navigate to='../settings/api-key' replace />}
              />
              <Route path='members'>
                <Route path=':targetOrgId' element={<MembersByOrgPage />} />
              </Route>
              {/* Personal API tokens — user-scoped PATs for the Upload API,
                  so they sit under `account` rather than the org-scoped
                  `settings` block, and are not wrapped in OwnerOrAdminRoute:
                  any authenticated org member manages their own tokens. */}
              <Route path='account'>
                <Route
                  path='api-tokens'
                  element={
                    <FeatureGuard isEnabled={isUserApiTokensEnabled}>
                      <ApiTokensPage />
                    </FeatureGuard>
                  }
                />
                <Route path='connectors' element={<ConnectorsPage />} />
              </Route>
              {/* Kept so links issued before the move still resolve. */}
              <Route
                path='settings/api-tokens'
                element={<Navigate to='../account/api-tokens' replace />}
              />
              <Route path='settings'>
                <Route element={<OwnerOrAdminRoute />}>
                  <Route path='general' element={<GeneralSettingsPage />} />
                  <Route path='members' element={<MembersPage />} />
                  <Route
                    path='api-key'
                    element={
                      <FeatureGuard isEnabled={isApiKeysEnabled}>
                        <CapitolKeysPage />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path='nodes'
                    element={
                      <FeatureGuard isEnabled={isOrgNodesToolsEnabled}>
                        <OrgNodesSettingsPage />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path='my-tools'
                    element={
                      <FeatureGuard isEnabled={isOrgMcpToolsEnabled}>
                        <MyToolsPage />
                      </FeatureGuard>
                    }
                  />
                  {/* Capitol MCP Gateway (ENG-5304): org-admin-only by the
                      surrounding OwnerOrAdminRoute; the backend CRUD
                      re-enforces admin server-side regardless. */}
                  <Route path='mcp-servers' element={<McpServersPage />} />
                  <Route
                    path='mcp-servers/:serverId'
                    element={<McpServerDetailPage />}
                  />
                  <Route
                    path='labels'
                    element={
                      <FeatureGuard isEnabled={isLabelsEnabled}>
                        <LabelsPage />
                      </FeatureGuard>
                    }
                  />
                </Route>
                {/* Deliberately outside OwnerOrAdminRoute: is_infra_admin is a
                    capability, not a role, so nesting these redirected a
                    legitimate infra-admin to `/`. */}
                <Route element={<InfraAdminRoute />}>
                  <Route
                    path='encryption'
                    element={<EncryptionSettingsPage />}
                  />
                  <Route
                    path='a2a-ip-allowlist'
                    element={<A2AIpAllowlistPage />}
                  />
                </Route>
              </Route>
              <Route element={<OwnerOrAdminRoute />}>
                {/* Guardrails configuration is org-wide governance and is
                    owner/admin-only. */}
                <Route path='guardrails' element={<GuardrailsPage />} />
                <Route
                  path='docs/workflow-api'
                  element={
                    <FeatureGuard isEnabled={isDocsWorkflowApiEnabled}>
                      <Suspense
                        fallback={
                          <div className='p-6 text-sm text-muted-foreground'>
                            Loading API specification...
                          </div>
                        }
                      >
                        <WorkflowApiPage />
                      </Suspense>
                    </FeatureGuard>
                  }
                />
              </Route>
              <Route path='composer-dashboard' element={<OwnerOrAdminRoute />}>
                <Route
                  index
                  element={
                    <FeatureGuard isEnabled={isComposerDashboardEnabled}>
                      <ComposerDashboardPage />
                    </FeatureGuard>
                  }
                />
              </Route>
              <Route
                path='evaluation'
                element={<FeatureGuard isEnabled={isEvalsEnabled} />}
              >
                <Route index />
                <Route path='evals' element={<EvaluationDashboard />} />
                <Route
                  path='evals/:evalId'
                  element={<EvaluationConfigPage />}
                />
                <Route path='tests' element={<TestsPage />} />
                <Route path='tests/:testId' element={<TestsConfigPage />} />
              </Route>
              <Route
                path='skills'
                element={
                  <FeatureGuard isEnabled={isSkillsEnabled}>
                    <SkillsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='artifacts'
                element={
                  <FeatureGuard isEnabled={isArtifactsEnabled}>
                    <ArtifactsPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='artifacts/:artifactId'
                element={
                  <FeatureGuard isEnabled={isArtifactsEnabled}>
                    <ArtifactViewerPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='files'
                element={
                  <FeatureGuard isEnabled={isFilesEnabled}>
                    <FilesPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='files/:repo/*'
                element={
                  <FeatureGuard isEnabled={isFilesEnabled}>
                    <FileBrowserPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='schedules'
                element={
                  <FeatureGuard isEnabled={isScheduleEnabled}>
                    <SchedulesPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='triggers'
                element={
                  <FeatureGuard isEnabled={isTriggersEnabled}>
                    <TriggersPage />
                  </FeatureGuard>
                }
              />
              <Route path='admin' element={<AdminRoute />}>
                <Route path='orgs' element={<AdminPage />} />
                <Route path='orgs/:targetOrgId' element={<OrgDetailsPage />} />
                <Route
                  path='analytics-config'
                  element={<AnalyticsConfigPage />}
                />
                <Route
                  path='workflow-agent'
                  element={<WorkflowAgentConfigPage />}
                />
                <Route path='cost' element={<CostPage />} />
                <Route
                  path='uat'
                  element={
                    <FeatureGuard isEnabled={isQualificationEnabled}>
                      <UatBoardPage />
                    </FeatureGuard>
                  }
                />
              </Route>
              <Route
                path='sources'
                element={
                  <FeatureGuard isEnabled={isSourcesEnabled}>
                    <SourcesPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='data-sources'
                element={
                  <FeatureGuard isEnabled={isDataSourcesEnabled}>
                    <DataSourcesPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='data-sources/:collectionId'
                element={
                  <FeatureGuard isEnabled={isDataSourcesEnabled}>
                    <CollectionPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='data-sources/:collectionId/datasets'
                element={
                  <FeatureGuard isEnabled={isDataSourcesEnabled}>
                    <CollectionPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='data-sources/:collectionId/datasets/:datasetId'
                element={
                  <FeatureGuard isEnabled={isDataSourcesEnabled}>
                    <CollectionPage />
                  </FeatureGuard>
                }
              />
              <Route
                path='context-graphs'
                element={
                  <FeatureGuard isEnabled={isContextGraphsEnabled}>
                    <ContextGraphsPage />
                  </FeatureGuard>
                }
              />
              <Route path='*' element={<NotFoundPage />} />
            </Route>
          </Route>

          {/* Legacy redirects: old flat URLs → org-scoped URLs */}
          <Route path='/' element={<OrgRedirect />} />
          <Route path='/workflow/*' element={<OrgRedirect />} />
          <Route path='/schedules/*' element={<OrgRedirect />} />
          <Route path='/data-sources/*' element={<OrgRedirect />} />
          <Route path='/context-graphs/*' element={<OrgRedirect />} />
          <Route path='/workflow-builder' element={<OrgRedirect />} />
          <Route path='/chat-agents/*' element={<OrgRedirect />} />
          <Route path='/agents/*' element={<OrgRedirect />} />
          <Route path='/settings/*' element={<OrgRedirect />} />
          <Route path='/account/*' element={<OrgRedirect />} />
          <Route path='/evaluation/*' element={<OrgRedirect />} />
          <Route path='/guardrails/*' element={<OrgRedirect />} />
          <Route path='/composer-dashboard' element={<OrgRedirect />} />
          <Route path='/docs/*' element={<OrgRedirect />} />
          <Route path='/members/*' element={<OrgRedirect />} />
          <Route path='/admin/*' element={<OrgRedirect />} />
          <Route path='/keys/*' element={<OrgRedirect />} />
        </Route>

        <Route path='*' element={<NotFoundPage />} />
      </SentryRoutes>
    </RouterWithErrorBoundary>
  );
};

export const MainRoutes = () => {
  return (
    <BrowserRouter>
      <MainRoutesContent />
    </BrowserRouter>
  );
};
